<script setup>

    import { ref, watch, onMounted, onUnmounted } from 'vue'

    import { storeToRefs } from 'pinia'
    import { useViewStore } from '@/Stores'

    import { VOnboardingWrapper, VOnboardingStep, useVOnboarding } from 'v-onboarding'
    import {
        IconArrowCounterclockwise20Filled,
        IconPrevious20Filled,
        IconNext20Filled,
        IconCheckmarkCircle20Filled,
        IconDismiss20Filled
    } from '@iconify-prerendered/vue-fluent'

    const viewStore = useViewStore()
    const { tourSteps, tourActive, tourStart, tourFinish, tourGoToStep } = storeToRefs(viewStore)

    const tourWrapper = ref(null)

    const { start, finish, goToStep } = useVOnboarding(tourWrapper)

    tourStart.value = start
    tourFinish.value = finish
    tourGoToStep.value = goToStep

    // Store current tour navigation functions for keyboard shortcuts
    const currentNext = ref(null)
    const currentPrevious = ref(null)
    const currentIsFirst = ref(true)
    const currentIsLast = ref(false)

    // Keyboard navigation handler
    function handleKeyPress(event) {
        if (!tourActive.value) return

        switch (event.key) {
            case 'ArrowLeft':
                if (currentPrevious.value && !currentIsFirst.value) {
                    event.preventDefault()
                    currentPrevious.value()
                }
                break
            case 'ArrowRight':
                if (currentNext.value && !currentIsLast.value) {
                    event.preventDefault()
                    currentNext.value()
                }
                break
            case 'Escape':
                event.preventDefault()
                viewStore.finishTour()
                break
        }
    }

    // Set up keyboard listener when tour becomes active
    watch(tourActive, (isActive) => {
        if (isActive) {
            document.addEventListener('keydown', handleKeyPress)
        } else {
            document.removeEventListener('keydown', handleKeyPress)
        }
    })

    // Clean up on unmount
    onUnmounted(() => {
        document.removeEventListener('keydown', handleKeyPress)
    })

    const globalOptions = {
        popper: {
            placement: 'auto',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                    offset: [0, 50]  // 16px distance from element
                    }
                }
            ]
        },
        overlay: {
            enabled: true,
            padding: 24,
            borderRadius: 12,
            preventOverlayInteraction: true
        },
        scrollToStep: {
            enabled: true,
            options: {
                behavior: 'smooth',
                block: 'center'
            }
        },
        autoFinishByExit: true,
        disableInteraction: true,
    }

</script>

<template>

    <VOnboardingWrapper
        ref="tourWrapper"
        :steps="tourSteps"
        @exit="viewStore.finishTour()"
        :options="globalOptions"
    >
        <template #default="{ step, next, previous, exit, isFirst, isLast, index }">
            <VOnboardingStep>
                    <div class="custom-step-card ">
                    <div class="p-3 rounded-xl border-[8px] border-lifeworx-blue-300 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)]">
                        <div class="flex flex-col max-w-[480px] rounded-lg overflow-hidden bg-white pb-2">
                            <!-- Capture slot props for keyboard navigation -->
                            <span style="display: none;">
                                {{ currentNext = next }}
                                {{ currentPrevious = previous }}
                                {{ currentIsFirst = isFirst }}
                                {{ currentIsLast = isLast }}
                            </span>

                            <!-- Content area -->
                            <div class="p-6 pb-4">
                                <!-- Step indicator -->
                                <div class="flex items-center gap-2 mb-3">
                                    <span class="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                        Step {{ step.options?.stepIndex || 1 }} of {{ step.options?.totalSteps || 1 }}
                                    </span>
                                </div>

                                <h3 class="text-gray-900 text-xl font-semibold mb-2">{{ step.content?.title }}</h3>
                                <p class="text-lifeworx-blue-900 text-base leading-relaxed tracking-wide">{{ step.content?.description }}</p>
                            </div>

                            <!-- Control buttons row -->
                            <div class="px-6 flex items-center gap-4 justify-evenly">

                                <!-- Start Over -->
                                <button
                                    @click="goToStep(0)"
                                    class="flex-grow-0 flex items-center justify-center p-3 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-md transition-colors"
                                    title="Start Over"
                                >
                                    <IconArrowCounterclockwise20Filled class="size-5" />
                                    <span class="sr-only">Restart Tour</span>
                                </button>

                                <!-- Previous -->
                                <button
                                    @click="previous"
                                    :disabled="isFirst"
                                    :class="[
                                        'flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold rounded-md transition-colors',
                                        isFirst
                                            ? 'text-gray-300 pointer-events-none'
                                            : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                                    ]"
                                >
                                    <IconPrevious20Filled class="size-5" />
                                    <span class="hidden sm:inline">Previous</span>
                                </button>

                                <!-- Next -->
                                <button
                                    v-if="!isLast"
                                    @click="next"
                                    class="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-lifeworx-blue-600 hover:bg-lifeworx-blue-700 rounded-md transition-colors"
                                >
                                    <span class="hidden sm:inline">Next</span>
                                    <IconNext20Filled class="size-5" />
                                </button>

                                <!-- Finish (only on last step) -->
                                <button
                                    v-else
                                    @click="viewStore.finishTour()"
                                    class="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-lifeworx-green-600 hover:bg-lifeworx-green-700 rounded-md transition-colors"
                                >
                                    <span class="hidden sm:inline">Finish</span>
                                    <IconCheckmarkCircle20Filled class="size-5" />
                                </button>

                                <!-- Exit -->
                                <button
                                    @click="viewStore.finishTour()"
                                    class="flex-grow-0 flex items-center justify-center p-3 text-sm font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-md transition-colors"
                                    title="Exit Tour"
                                >
                                    <IconDismiss20Filled class="size-5" />
                                    <span class="sr-only">Exit Tour</span>
                                </button>
                            </div>

                            <!-- Progress bar -->
                            <div class="h-4 m-4 bg-lifeworx-blue-600/15 rounded-lg overflow-hidden">
                                <div
                                    class="h-full bg-lifeworx-blue-600"
                                    :style="{ width: `${((step.options?.stepIndex || 1) / (step.options?.totalSteps || 1)) * 100}%` }"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </VOnboardingStep>

        </template>
    </VOnboardingWrapper>

</template>

<style>

    /* Popper arrow styled to match the blue border ring - unscoped to target library-generated element */
    .custom-step-card ~ [data-popper-arrow] {
        z-index: 20010 !important; /* tour steps + 10 */
    }
    .custom-step-card ~ [data-popper-arrow]::before {
        background: #26d5ff !important; /* lifeworx-blue-300 */
    }

</style>
